import { BidAsk, BidsAsks } from '../types/orderbook';
import { Gwei } from './gwei';
import { divide, multiply } from './math';
import { calcMidPriceFromOrderbook, calcPriceImpact } from './orderbookHelpers';

export type DepthData = {
  price: bigint;
  size: bigint;
  depth: bigint;
  totalCost: bigint;
  priceImpact: bigint;
  type: 'bid' | 'ask';
};

export function convertBidsAsksToDepthData(
  bidsAsks: BidsAsks,
  maxPriceImpactPct?: bigint
): DepthData[] {
  const midPrice = calcMidPriceFromOrderbook(bidsAsks);
  if (midPrice === null) {
    return [];
  }
  function processOrders(orders: BidAsk[], type: 'bid' | 'ask', midPrice: bigint) {
    let depth = 0n;
    let totalCost = 0n;

    return orders.map(([price, size]) => {
      depth += size;
      totalCost += multiply(price, size);
      const priceImpact = calcPriceImpact(price, midPrice, type) ?? 0n;

      return {
        price,
        size,
        depth,
        totalCost,
        priceImpact,
        type,
      };
    });
  }

  const bids = processOrders(bidsAsks.bids, 'bid', midPrice);
  const asks = processOrders(bidsAsks.asks.slice().reverse(), 'ask', midPrice);
  const data = [...bids, ...asks];
  if (maxPriceImpactPct === undefined) {
    return data;
  }
  return data.filter((x) => x.priceImpact <= maxPriceImpactPct);
}

const createLinearScale = ({ min, max }: { min: bigint; max: bigint }) => {
  if (min >= max) {
    return () => 0n;
  }

  return (value: bigint): bigint => {
    return Gwei.min(Gwei.toGwei(1), Gwei.max(0n, divide(value - min, max - min)));
  };
};

export const getDepthScale = (
  bids: BidAsk[] | undefined,
  asks: BidAsk[] | undefined,
  marketDepth: DepthData[] | undefined
): ((value: bigint) => bigint) => {
  if (!bids?.length || !asks?.length || !marketDepth) {
    return () => 0n;
  }

  const bidPrices = bids.map((bid) => bid[0]);
  const askPrices = asks.map((ask) => ask[0]);

  const worstBidPrice = bidPrices.reduce((min, price) => Gwei.min(min, price));
  const worstAskPrice = askPrices.reduce((max, price) => Gwei.max(max, price));

  const filteredBids = marketDepth.filter(
    (point) => point.type === 'bid' && point.price >= worstBidPrice
  );

  const filteredAsks = marketDepth.filter(
    (point) => point.type === 'ask' && point.price <= worstAskPrice
  );

  if (!filteredBids.length && !filteredAsks.length) {
    return () => 0n;
  }

  const allDepths = [...filteredBids, ...filteredAsks].map((point) => point.depth);
  const minMaxDepth = {
    min: 0n,
    max: allDepths.reduce((max, d) => Gwei.max(max, d)),
  };

  // Use log scale by default
  return createLinearScale(minMaxDepth);
};
